import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HYDRATE } from 'next-redux-wrapper';
import { createRoutine } from 'redux-saga-routines';
import { filterHotspotVisible } from '@utils/helper';
import { ContainerState, I_LanguageObject } from './typesIndex';
import { I_Hotspot, I_Project, I_Scene, I_TourRouteConverted } from './types';
import { I_Group } from './types/I_Group';

// The initial state of the App container
export const initialState: ContainerState = {
  loading: false,
  success: false,
  error: false,
  loadingLanguage: 0,
  errorLanguage: false,

  dataMap: null,
  listFloorplan: [],
  listTourRoute: [],

  configMap: null,
  configLogo: null,
  configNadir: null,
  configCopyRight: null,
  configFloorplan: null,
  configPopupIntro: null,
  configProtectPassword: null,

  listScene: [],
  listGroup: [],
  listLanguage: [],
  listMediaVideoPlay: [],
  listHotspotSoundPlay: [],
  listHotspotInstructorPlay: [],
  listCallToAction: [],

  project: null,

  isFirstScene: null,
  configSoundProject: {
    on: null,
  },

  configAutoRotate: {
    on: false,
    enabled: false,
    speed: 2,
    waitTime: 2,
  },
  configAutoChangeScene: {
    on: false,
    enabled: false,
    waitTime: 15,
  },

  token: null,

  sceneSelected: null,
  hotspotSelected: null,
  hotspotSelectedOpenIframe: null,
  tourRouteSelected: null,
  prevSceneSelected: null,
  languageSelected: null,
  isChangeLanguage: false,
  isLanguageValid: false,
};

export const GET_DETAIL_PROJECT = createRoutine('App/getDetailProject');
export const GET_LIST_LANGUAGE = createRoutine('App/getListLanguage');

// Get list hotspot sound and media video in scene selected
const updateListMediaAndSound = (state: ContainerState) => {
  const { media, sound, instructor } = state.sceneSelected?.hotspots.reduce(
    (result, item) => {
      if (item?.config?.media?.type === 'media-video') {
        if (
          (state.languageSelected &&
            item?.language?.[state.languageSelected]?.config?.media?.media) ||
          !state.languageSelected
        )
          result.media.push({ hotspot: item, play: false });
      } else if (item.type === 'sound') {
        if (
          (state.languageSelected &&
            item?.language?.[state.languageSelected]?.config?.sound?.file) ||
          !state.languageSelected
        )
          result.sound.push({
            hotspot: item,
            play: item?.config?.sound?.auto_play,
            old: item?.config?.sound?.auto_play,
          });
      } else if (item.type === 'instructor') {
        if (
          (state.languageSelected &&
            item?.language?.[state.languageSelected]?.config?.instructor?.media
              ?.src) ||
          !state.languageSelected
        )
          result.instructor.push({ hotspot: item, play: false });
      }
      return result;
    },
    { media: [], sound: [], instructor: [] },
  );

  return { media, sound, instructor };
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setSceneSelected(
      state: ContainerState,
      action: PayloadAction<I_Scene | null>,
    ) {
      if (action.payload)
        if (state.isFirstScene === null) state.isFirstScene = true;
        else state.isFirstScene = false;

      state.sceneSelected = {
        ...action.payload,
        hotspots:
          action.payload?.hotspots?.filter(item =>
            filterHotspotVisible(item),
          ) || [],
      };

      const { media, sound, instructor } = updateListMediaAndSound(state);
      state.listMediaVideoPlay = media;
      state.listHotspotSoundPlay = sound;
      state.listHotspotInstructorPlay = instructor;

      // Initialize sound hotspots when a scene is selected
      if (action.payload.hotspots) {
        const soundHotspots = action.payload.hotspots.filter(
          hotspot => hotspot.type === 'sound' && hotspot.config?.sound?.file,
        );

        state.listHotspotSoundPlay = soundHotspots.map(hotspot => ({
          hotspot,
          play: hotspot.config?.sound?.auto_play || false,
          old: hotspot.config?.sound?.auto_play || false,
        }));
      }
    },

    setTourRouteSelected(
      state: ContainerState,
      action: PayloadAction<I_TourRouteConverted | undefined>,
    ) {
      state.tourRouteSelected = action.payload;
    },

    setListMediaVideoPlay(state: ContainerState, action: PayloadAction<any>) {
      state.listMediaVideoPlay = action.payload;
    },

    setListHotspotSoundPlay(state: ContainerState, action: PayloadAction<any>) {
      state.listHotspotSoundPlay = action.payload;
    },

    setListHotspotInstructorPlay(
      state: ContainerState,
      action: PayloadAction<any>,
    ) {
      state.listHotspotInstructorPlay = action.payload;
    },

    setHotspotSelectedOpenIframe(
      state: ContainerState,
      action: PayloadAction<any>,
    ) {
      state.hotspotSelectedOpenIframe = action.payload;
    },

    setToken(state: ContainerState, action: PayloadAction<string | null>) {
      state.token = action.payload;
    },

    setLanguageSelected(
      state: ContainerState,
      action: PayloadAction<string | null>,
    ) {
      state.languageSelected = action.payload;
      if (state.sceneSelected) {
        const { media, sound, instructor } = updateListMediaAndSound(state);
        state.listMediaVideoPlay = media;
        state.listHotspotSoundPlay = sound;
        state.listHotspotInstructorPlay = instructor;
      }
    },

    setIsChangeLanguage(state: ContainerState) {
      state.isChangeLanguage = true;
    },

    setIsLanguageValid(state: ContainerState, action: PayloadAction<boolean>) {
      state.isLanguageValid = action.payload;
    },

    setIsFirstScene(state: ContainerState, action: PayloadAction<boolean>) {
      state.isFirstScene = action.payload;
    },

    setConfigSoundProject(
      state: ContainerState,
      action: PayloadAction<Partial<ContainerState['configSoundProject']>>,
    ) {
      const { on } = action.payload;
      if (on !== undefined) state.configSoundProject.on = on;
    },

    setConfigAutoChangeScene(
      state: ContainerState,
      action: PayloadAction<Partial<ContainerState['configAutoChangeScene']>>,
    ) {
      const { enabled, waitTime, on } = action.payload;
      if (on !== undefined) state.configAutoChangeScene.on = on;
      if (enabled !== undefined) state.configAutoChangeScene.enabled = enabled;
      if (waitTime !== undefined)
        state.configAutoChangeScene.waitTime = waitTime;
    },

    setConfigAutoRotate(
      state: ContainerState,
      action: PayloadAction<Partial<ContainerState['configAutoRotate']>>,
    ) {
      const { on, enabled, waitTime, speed } = action.payload;
      if (on !== undefined) state.configAutoRotate.on = on;
      if (enabled !== undefined) state.configAutoRotate.enabled = enabled;
      if (waitTime !== undefined) state.configAutoRotate.waitTime = waitTime;
      if (speed !== undefined) state.configAutoRotate.speed = speed;
    },

    setprevSceneSelected(
      state: ContainerState,
      action: PayloadAction<I_Scene | null>,
    ) {
      state.prevSceneSelected = action.payload;
    },

    setHotspotSelected(
      state: ContainerState,
      action: PayloadAction<I_Hotspot | null>,
    ) {
      state.hotspotSelected = action.payload;
    },

    setListScene(state: ContainerState, action: PayloadAction<I_Scene[]>) {
      state.listScene = action.payload || [];
    },

    resetStatusPage(state) {
      state.loading = initialState.loading;
      state.error = initialState.error;
      state.success = initialState.success;
    },
  },
  extraReducers: {
    [HYDRATE]: (_state: ContainerState, action: PayloadAction<any>) =>
      action.payload.app,

    /**
     * ! GET DETAIL PROJECT
     */
    [GET_DETAIL_PROJECT.TRIGGER]: (
      state,
      // action: PayloadAction<{ id: string }>,
    ) => {
      state.loading = true;
      state.success = false;
      state.error = false;
    },
    [GET_DETAIL_PROJECT.SUCCESS]: (state, action: PayloadAction<I_Project>) => {
      const project = action.payload;
      const { scenes: listScene = [], groups = [], plugins = [] } = project;

      const scenes =
        listScene
          .filter(el => !el.parent && el.media)
          .map(item => ({
            ...item,
            staging:
              listScene
                ?.filter(el => el.parent && el.parent === item.id)
                // Because the array is frozen in strict mode, you'll need to copy the array before sorting it: (https://stackoverflow.com/questions/53420055/error-while-sorting-array-of-objects-cannot-assign-to-read-only-property-2-of/53420326)
                ?.filter(el => el.mode_in_parent === 'staging')
                ?.slice()
                ?.sort((a, b) =>
                  a.sort_in_parent === b.sort_in_parent
                    ? Math.abs(
                        new Date(a.updated_at).valueOf() -
                          new Date(b.updated_at).valueOf(),
                      )
                    : a.sort_in_parent - b.sort_in_parent,
                ) || [],
            day2dust:
              listScene
                ?.filter(el => el.parent && el.parent === item.id)
                ?.filter(el => el.mode_in_parent === 'day2dust') || [],
          })) || [];

      state.listScene =
        scenes.sort((a, b) =>
          a.sort === b.sort
            ? Math.abs(
                new Date(a.updated_at).valueOf() -
                  new Date(b.updated_at).valueOf(),
              )
            : a.sort - b.sort,
        ) || [];

      const groupUncategory: I_Group = {
        id: 'uncategory',
        name: 'Uncategory',
        sort: 0,
        scenes: scenes
          .filter(item => !item.group)
          .filter(item => item.publish)
          .sort((a, b) =>
            a.sort === b.sort
              ? Math.abs(
                  new Date(a.updated_at).valueOf() -
                    new Date(b.updated_at).valueOf(),
                )
              : a.sort - b.sort,
          ),
      };

      state.listGroup = [groupUncategory]
        .concat(
          groups.map(item => ({
            ...item,
            scenes:
              scenes
                .filter(scene => scene.group === item.id)
                .filter(scene => scene.publish)
                .sort((a, b) =>
                  a.sort === b.sort
                    ? Math.abs(
                        new Date(a.updated_at).valueOf() -
                          new Date(b.updated_at).valueOf(),
                      )
                    : a.sort - b.sort,
                ) || [],
          })),
        )
        .filter(item => item.scenes.length);

      state.project = project;

      // NOTE: Plugins
      const pluginMap = plugins.filter(item => item.title === 'map')[0];
      const pluginLogo = plugins.filter(item => item.title === 'logo')[0];
      const pluginNadir = plugins.filter(item => item.title === 'nadir')[0];
      const pluginPopupIntro = plugins.filter(
        item => item.title === 'popupIntro',
      )[0];
      const pluginFloorplan = plugins.filter(
        item => item.title === 'floorplan',
      )[0];
      const pluginTourRoute = plugins.filter(
        item => item.title === 'tour-route',
      )[0];
      const pluginProtectPassword = plugins.filter(
        item => item.title === 'protectPassword',
      )[0];
      const pluginCopyRight = plugins.filter(
        item => item.title === 'copyRight',
      )[0];
      const pluginCallToAction = plugins.filter(
        item => item.title === 'callToAction',
      )[0];

      state.dataMap =
        pluginMap?.title === 'map' ? pluginMap?.config?.data || null : null;

      state.configMap =
        pluginMap?.title === 'map' ? pluginMap?.config?.config || null : null;

      state.configLogo =
        pluginLogo?.title === 'logo'
          ? pluginLogo?.config?.config || null
          : null;

      state.configNadir =
        pluginNadir?.title === 'nadir'
          ? pluginNadir?.config?.config || null
          : null;

      state.configPopupIntro =
        pluginPopupIntro?.title === 'popupIntro'
          ? pluginPopupIntro?.config?.config || null
          : null;

      state.configProtectPassword =
        pluginProtectPassword?.title === 'protectPassword'
          ? pluginProtectPassword?.config?.config || null
          : null;

      state.configCopyRight =
        pluginCopyRight?.title === 'copyRight'
          ? pluginCopyRight?.config?.config || null
          : null;

      state.listTourRoute =
        pluginTourRoute?.title === 'tour-route'
          ? pluginTourRoute?.config?.data
              .map(item => ({
                ...item,
                scenes:
                  item.scenes
                    .map(id => scenes.find(scene => scene.id === id))
                    .filter(scene => !!scene) || [],
              }))
              .filter(item => item.scenes.filter(scene => !!scene)) || []
          : [];

      state.listFloorplan =
        pluginFloorplan?.title === 'floorplan'
          ? pluginFloorplan?.config?.data?.filter(
              item => item.name && item.src && item.publish,
            ) || []
          : [];

      state.configFloorplan =
        pluginFloorplan?.title === 'floorplan'
          ? pluginFloorplan?.config?.config || null
          : null;

      state.listCallToAction =
        pluginCallToAction?.title === 'callToAction'
          ? pluginCallToAction?.config?.data || null
          : null;

      // NOTE: config globally
      state.configAutoChangeScene.on =
        project.config?.setting?.auto_change_scene?.enable || false;
      state.configAutoChangeScene.enabled =
        project.config?.setting?.auto_change_scene?.enable || false;
      state.configAutoChangeScene.waitTime =
        project.config?.setting?.auto_change_scene?.wait_time || 2;

      state.configAutoRotate.on =
        project.config?.setting?.auto_rotate?.enable || false;
      state.configAutoRotate.enabled =
        project.config?.setting?.auto_rotate?.enable || false;
      state.configAutoRotate.speed =
        project.config?.setting?.auto_rotate?.speed || 2;
      state.configAutoRotate.waitTime =
        project.config?.setting?.auto_rotate?.wait_time || 2;

      state.loading = false;
      state.success = true;
      state.error = false;
    },
    [GET_DETAIL_PROJECT.FAILURE]: state => {
      state.loading = false;
      state.success = false;
      state.error = true;
    },

    /**
     * ! GET_LIST_LANGUAGE
     */
    [GET_LIST_LANGUAGE.TRIGGER]: state => {
      state.loadingLanguage += 1;
      state.errorLanguage = false;
    },
    [GET_LIST_LANGUAGE.SUCCESS]: (
      state,
      action: PayloadAction<I_LanguageObject[]>,
    ) => {
      state.listLanguage = action.payload;
      state.loadingLanguage -= 1;
      state.errorLanguage = false;
    },
    [GET_LIST_LANGUAGE.FAILURE]: state => {
      state.loadingLanguage = 0;
      state.errorLanguage = true;
    },
  },
});

export const { actions, reducer, name: sliceKey } = appSlice;

export default reducer;
